import { useEffect, useRef } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import type { AnalyticsResult } from '../../services/analyticsService';
import { formatCell } from './analyticsFormat';

/** Rows rendered in the table; the full result stays available for the chart and CSV. */
export const TABLE_ROW_LIMIT = 200;

export interface ResultTableProps {
  result: AnalyticsResult;
  caption: string;
}

/**
 * Result rows as a data table. The wrapper scrolls horizontally on narrow screens
 * (WCAG 1.4.10 allows it for data tables) and is focusable while it overflows.
 */
export function ResultTable({ result, caption }: ResultTableProps) {
  const { t, i18n } = useTranslation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const rows = result.rows.slice(0, TABLE_ROW_LIMIT);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (wrap === null) return undefined;
    const update = (): void => {
      if (wrap.scrollWidth > wrap.clientWidth + 1) wrap.setAttribute('tabindex', '0');
      else wrap.removeAttribute('tabindex');
    };
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(wrap);
    return () => observer.disconnect();
  });

  return (
    <div ref={wrapRef} className="analytics-table__wrap" role="region" aria-label={caption}>
      <table className="analytics-table">
        <caption className="analytics-table__caption">
          {caption}
          {result.rows.length > TABLE_ROW_LIMIT && ` — ${t('analytics.result.tableLimited', { shown: TABLE_ROW_LIMIT, total: result.rows.length })}`}
        </caption>
        <thead>
          <tr>
            {result.columns.map((c) => (
              <th key={c.name} scope="col" className={c.kind === 'number' ? 'analytics-table__num' : undefined}>{c.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((value, j) => (
                <td key={result.columns[j].name} className={result.columns[j].kind === 'number' ? 'analytics-table__num' : undefined}>
                  {formatCell(value, result.columns[j], i18n.language)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ResultTable;
